import { UserDTO } from '../../dto/users/user.dto';
import { UserLocationDTO } from '../../dto/users/user-location.dto';
import { UserStatusDTO } from '../../dto/users/user-status.dto';
import { EducationalPathUserDTO } from '../../dto/educational-path/educational-path-user.dto';
import { LocationDTO } from '../../dto/location/location.dto';

import { UserGroupSummaryItem } from '../../dto/user-group/user-group.dto';

export class User {
    constructor(
        public uuid: string,
        public first_name: string,
        public last_name: string,
        public user: string | null,
        public roles: {
            bruger?: string[];
            primaer?: string[];
            superadmin?: boolean;
            [key: string]: any;
        } | null,
        public organizations_id: number | null,
        public locations: UserLocationDTO[] | null,
        public status: UserStatusDTO | null,
        public groups: UserGroupSummaryItem[] | null = null
    ) {}

    get name(): string {
        return `${this.first_name} ${this.last_name}`.trim();
    }

    get primaryLocation(): string {
        if (this.locations && this.locations.length > 0) {
            return this.locations[0].locations_name;
        }
        if (this.roles && this.roles.primaer && this.roles.primaer.length > 0) {
            return this.roles.primaer[0];
        }
        return 'Ingen lokation';
    }

    get ok_status(): boolean | undefined {
        if (!this.status) return undefined;
        return this.status.validation_status === 'valid';
    }

    get validation_status(): string {
        return this.status?.validation_status ?? 'no_data';
    }

    get registered_percentage(): number {
        return this.status?.registered_percentage ?? 0;
    }

    get registeredPercentage(): number {
        return this.status?.registered_percentage ?? 0;
    }

    get progress_percentage(): number {
        return this.status?.progress_percentage ?? 0;
    }

    get percentComplete(): number {
        return this.status?.progress_percentage ?? 0;
    }

    get educational_paths(): EducationalPathUserDTO[] {
        return this.status?.educational_paths ?? [];
    }

    get requirement_paths(): EducationalPathUserDTO[] {
        return this.educational_paths.filter(path => path.commitment_type === 'requirement');
    }

    get maintenance_paths(): EducationalPathUserDTO[] {
        return this.educational_paths.filter(path => path.commitment_type === 'maintenance');
    }

    get total_paths_completed(): number {
        return this.educational_paths.filter(path => {
            return path.progress_percentage === 100 && path.completed_items === path.total_items;
        }).length;
    }

    get total_paths_in_progress(): number {
        return this.educational_paths.filter(path => {
            const isCompleted = path.progress_percentage === 100 && path.completed_items === path.total_items;
            const hasProgress = path.progress_percentage > 0 || path.completed_items > 0;
            return hasProgress && !isCompleted;
        }).length;
    }

    get total_items_completed(): number {
        return this.educational_paths.reduce((total, path) => total + path.completed_items, 0);
    }

    get total_items(): number {
        return this.educational_paths.reduce((total, path) => total + path.total_items, 0);
    }

    static fromDTO(dto: UserDTO): User {
        const locations = dto.locations?.map(loc => ({
            id: 0,
            guid: loc.guid,
            locations_name: loc.locations_name,
            role_name: 'default'
        } as UserLocationDTO)) || null;

        return new User(
            dto.uuid,
            dto.first_name,
            dto.last_name,
            dto.user,
            dto.roles,
            dto.organizations_id,
            locations,
            dto.status,
            dto.groups ?? null
        );
    }
} 